import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const TIMELINE = [
  { key: 'searchingDocs', duration: 6000 },
  { key: 'searchingWeb', duration: 5000 },
  { key: 'refining', duration: 5000 },
  { key: 'generating', duration: Infinity },
];

/**
 * Animated text component with wave effect
 * Each letter lights up in sequence (forward then backward)
 */
function WaveText({ text }) {
  const letters = text.split('');
  const totalLetters = letters.length;

  return (
    <span className="inline-flex">
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          className="inline-block"
          animate={{
            color: [
              '#787572', // text-muted (warm gray-brown base)
              '#ff9e1b', // primary orange (highlight)
              '#787572', // text-muted (warm gray-brown base)
            ],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: index * 0.08, // Stagger forward
            repeatDelay: (totalLetters - index) * 0.08, // Stagger backward
          }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </span>
  );
}

export function ChatLoadingIndicator() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const startTime = Date.now();

    const checkStep = () => {
      const elapsed = Date.now() - startTime;
      let cumulativeTime = 0;

      for (let i = 0; i < TIMELINE.length; i++) {
        cumulativeTime += TIMELINE[i].duration;
        if (elapsed < cumulativeTime || TIMELINE[i].duration === Infinity) {
          setCurrentStep(i);
          break;
        }
      }
    };

    const interval = setInterval(checkStep, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mt-2 h-8 flex items-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: -20, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 20, scale: 0.95 }}
          transition={{
            duration: 0.4,
            ease: "easeOut"
          }}
          className="text-[0.9rem] flex items-center gap-2.5"
        >
          <WaveText text={t(`chat.loading.${TIMELINE[currentStep].key}`)} />
          <motion.span className="inline-flex">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="icon-muted"
                animate={{ opacity: [0, 0.8, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
              >
                .
              </motion.span>
            ))}
          </motion.span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
